import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import AddSubCategory from './AddSubCategory';
import { AppState } from '../../../store';
import LinearProgressBar from '../../../common/LinearProgress';
import { onGetAllSubCategories } from '../../../store/actions/Category';
import jwtAxios from '../../../services/axiosInstance/ApiConfig';
import { getHeaders } from '../../../store/actions/Auth';
import { showErrorMessage, showSuccessMessage } from '../../../common/Toaster';

interface Column {
	id: 'id' | 'sub_name' | 'cat_id'| 'actions';
	label: string;
	minWidth?: number;
	align?: 'right';
	format?: (value: number) => string;
}

const columns: readonly Column[] = [
	{ id: 'id', label: 'Sub Category ID', minWidth: 170 },
	{ id: 'sub_name', label: 'Sub Category Name', minWidth: 170 },
	{ id: 'cat_id', label: 'Category Id', minWidth: 170 },
	{ id: 'actions', label: 'Actions', minWidth: 170 }
];

interface Data {
	id: string;
	sub_name: string;
	cat_id: string;
}

function createData(
	id: string,
	sub_name: string,
	cat_id: string
): Data {
	return { id, sub_name, cat_id };
}
interface SubCategoryProps {
	open: boolean;
	setOpen: (open: boolean) => void;
}

export default function SubCategories(props: SubCategoryProps) {
	const { open, setOpen } = props;
	
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
	
	const { subcategories } = useSelector<AppState, AppState['categories']>(
		({ categories }) => categories,
	);
	const { loading } = useSelector<AppState, AppState['common']>(
		({ common }) => common,
	);

	const dispatch = useDispatch()

	useEffect(() => {
		if(!subcategories)
			dispatch(onGetAllSubCategories())
	}, [dispatch, subcategories])

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const deletesubCategory = (rowId: string) => {
		jwtAxios.delete('/subcategory/delete_subcategory/' + rowId, { headers: getHeaders()})
		.then(response => {
			showSuccessMessage(response.data.message)
			dispatch(onGetAllSubCategories())
		}).catch((err: any) => {
			showErrorMessage(err)
		})
	}

	const handleOnSetOpen = (open: boolean) => {
		setOpen(open)
	}

	const rows: any =
		subcategories && subcategories.length > 0
			? subcategories.map((subc: any) => createData(subc._id, subc.name, subc.category_id))
			: [];

	return (
		<>
			{open &&
				<AddSubCategory setOpen={handleOnSetOpen} />
			}
			{!open &&
				<Paper sx={{ width: '100%', overflow: 'hidden', padding: 0 }}>
					<TableContainer>
						{loading && <LinearProgressBar />}
						<Table stickyHeader aria-label="sticky table">
							<TableHead>
								<TableRow>
									{columns.map((column) => {
										return (
											<TableCell
												key={column.id}
												align={column.align}
												style={{ minWidth: column.minWidth, fontWeight: 600 }}
											>
												{column.label}
											</TableCell>
										)}
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{rows
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((row: any) => {
										return (
											<TableRow hover role="checkbox" tabIndex={-1} key={row.sub_name}>
												{columns.map((column) => {
													const col_id = column.id
													let value = row[col_id];
													return (
														<TableCell key={column.id} align={column.align}>
															{col_id === 'actions' && 
																<Box display="flex" flexDirection="row">
																	<Button
																		color="info"
																		variant="outlined"
																		size="small"
																		style={{marginRight: '8px'}}
																		//onClick={()=> props.handleSelectedCategory(row)}
																	>
																		Edit
																	</Button>
																	<Button
																		color="error"
																		variant="outlined"
																		size="small"
																		onClick={()=> deletesubCategory(row.id)}
																	>
																		Delete
																	</Button>
																</Box>
															}
															{col_id !== 'actions' && column.format && typeof value === 'number'
																? column.format(value)
																: value}
														</TableCell>
													);
												})}
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[10, 25, 100]}
						component="div"
						count={rows.length}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Paper>
			}
		</>
	);
}
